import type { NavigateFunction } from "react-router-dom"
import type { Dispatch, SetStateAction } from "react"
import { AddressBar } from "@/components/ui/address-bar"
import type { OpenTab } from "@/components/features/document-tabs"
import type { PerTabNavigationApi } from "@/hooks/use-navigation-history"
import type { WorkspaceRouteInfo } from "@/lib/app-routes"
import { testIds } from "@/lib/test-ids"
import {
  buildDocumentFolderBreadcrumbSegments,
  isContentDocumentTab,
  resolveToolbarDocumentPath,
  shouldReserveDocumentBreadcrumbRow,
} from "./document-viewer.utils"

export type DocumentToolbarBreadcrumbsProps = {
  currentDocumentPath: string | null
  currentTab: OpenTab | undefined
  activeTabId: string
  containerWidth: number | null
  basecampTodoId: string | null
  workspaces: WorkspaceRouteInfo[]
  openTabs: OpenTab[]
  setOpenTabs: Dispatch<SetStateAction<OpenTab[]>>
  setActiveTabId: (id: string) => void
  navigation: PerTabNavigationApi
  navigate: NavigateFunction
  onFolderClick?: (folderPath: string, skipTabManagement?: boolean, historySlotId?: string) => void
}

export function DocumentToolbarBreadcrumbs({
  currentDocumentPath,
  currentTab,
  activeTabId,
  containerWidth,
  basecampTodoId,
  workspaces,
  openTabs,
  setOpenTabs,
  setActiveTabId,
  navigation,
  navigate,
  onFolderClick,
}: DocumentToolbarBreadcrumbsProps) {
  if (
    currentTab?.type === 'integration-basecamp-todo' ||
    currentTab?.type === 'integration-basecamp-triage' ||
    currentTab?.type === 'explorer' ||
    !isContentDocumentTab(currentTab, activeTabId)
  ) {
    return null
  }

  const toolbarDocumentPath = resolveToolbarDocumentPath(
    currentDocumentPath,
    currentTab,
    activeTabId,
  )
  const pathFetchPending = isContentDocumentTab(currentTab, activeTabId) && !currentDocumentPath

  // Path crumbs only. A Basecamp todo title used to lead the bar for linked docs and
  // read as a bogus path segment; jump-to-todo stays in the overflow menu instead.
  const breadcrumbSegments = toolbarDocumentPath
    ? buildDocumentFolderBreadcrumbSegments({
        documentPath: toolbarDocumentPath,
        workspaces,
        openTabs,
        activeTabId,
        setOpenTabs,
        setActiveTabId,
        navigation,
        navigate,
        onFolderClick,
      })
    : null

  const showBar = Boolean(breadcrumbSegments?.length)
  const reserveRow = shouldReserveDocumentBreadcrumbRow(currentTab, activeTabId, toolbarDocumentPath, {
    basecampTodoId,
    pathFetchPending,
  })

  if (showBar && breadcrumbSegments) {
    return (
      <div data-testid={testIds.documentViewer.breadcrumbs}>
        <AddressBar segments={breadcrumbSegments} containerWidth={containerWidth} />
      </div>
    )
  }

  if (reserveRow) {
    return (
      <div
        className="min-h-5 text-sm"
        aria-hidden
        data-testid={testIds.documentViewer.breadcrumbsPlaceholder}
      />
    )
  }

  return null
}
